import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  gray40,
  gray60,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularLg, regularMd, regularXl } from '../../../constants/location';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { AuthContext } from '../../../contexts/auth';
import { getUsersByCompanyId } from '../../../api/firebase/firestore/user/read';
import { updateAssignee } from '../../../api/firebase/firestore/result/updateAssignee';
import { bulkUpdateAssignee } from '../../../api/firebase/firestore/result/bulkUpdateAssignee';
import { registerStatus } from '../../../constants/enum';
import { assigneeLabels } from '../../../constants/assignee';
import type { AssigneeOption } from '../../../utils/assigneeDisplay';
import NoAccountIcon from '../../assets/Account circle.svg';

export type AssigneeChangeDialogMode = 'single' | 'bulk';

interface Props {
  open: boolean;
  onClose: () => void;
  mode: AssigneeChangeDialogMode;
  /** 変更対象の result_id 一覧。single の場合は length=1 */
  targetResultIds: string[];
  /** single 時のみ初期値設定に使用 */
  currentAssigneeId?: string;
  /** single 時の対象ファイル名（ダイアログ上部に表示） */
  targetLabel?: string;
  companyId: string;
  /**
   * 親で既に保持している担当者候補リスト（#2642 レビューFB）。
   * 渡された場合はダイアログ側で再フェッチせず、これをそのまま使う。
   */
  options?: AssigneeOption[];
  onSuccess?: (updatedCount: number) => void;
  onError?: (message: string) => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: `1px solid ${gray40}`,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      maxWidth: '480px',
      width: '100%',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  iconButton: {
    padding: 0,
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  select: {
    width: '100%',
    // 他の Select (AiOcrSearchForm の selectFiled) と統一: focus 時もデフォルトの青枠でなく gray100
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: gray60,
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
      borderWidth: '1px',
    },
  },
  menuItemBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  avatar: {
    width: 24,
    height: 24,
    fontSize: '12px',
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '16px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  confirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  confirmButtonText: {
    ...boldMd,
    color: 'white',
  },
};

/**
 * 担当者変更ダイアログ #2642
 *
 * - 単一(single) / 複数一括(bulk) を mode で切り替え
 * - 候補ユーザーは同一会社の register_status=finished のみ
 * - アクションボタン文言は「変更する」/ 処理中は「変更中...」
 */
const AssigneeChangeDialog = ({
  open,
  onClose,
  mode,
  targetResultIds,
  currentAssigneeId,
  targetLabel,
  companyId,
  options: optionsProp,
  onSuccess,
  onError,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();
  const { user } = useContext(AuthContext);

  const [options, setOptions] = useState<AssigneeOption[]>(optionsProp ?? []);
  const [loading, setLoading] = useState(false);
  const [selectedUid, setSelectedUid] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const fetchOptions = useCallback(async () => {
    if (!companyId) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const users = await getUsersByCompanyId(companyId);
      const list: AssigneeOption[] = users
        .filter(u => u.register_status === registerStatus.finished)
        .map(u => ({
          uid: u.uid as string,
          name: (u.name as string) ?? '',
          iconUrl: (u.icon_url as string | undefined) ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ja'));
      setOptions(list);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!open) return;
    setSelectedUid(currentAssigneeId ?? '');
    // 親から候補リストが渡された場合は再フェッチしない (#2642 レビューFB)
    if (optionsProp) {
      setOptions(optionsProp);
      return;
    }
    void fetchOptions();
  }, [open, currentAssigneeId, fetchOptions, optionsProp]);

  const handleConfirm = async (): Promise<void> => {
    if (!selectedUid || submitting || !user) return;
    const selected = options.find(o => o.uid === selectedUid);
    if (!selected) return;

    setSubmitting(true);
    try {
      if (mode === 'single') {
        const resultId = targetResultIds[0];
        if (!resultId) {
          throw new Error('対象データが指定されていません');
        }
        await updateAssignee({
          resultId,
          assigneeId: selected.uid,
          assigneeName: selected.name,
          assigneeIconUrl: selected.iconUrl,
          updatedBy: user.uid,
          updatedByName: user.name ?? '',
        });
        onSuccess?.(1);
      } else {
        const res = await bulkUpdateAssignee({
          resultIds: targetResultIds,
          assigneeId: selected.uid,
          assigneeName: selected.name,
          assigneeIconUrl: selected.iconUrl,
          updatedBy: user.uid,
          updatedByName: user.name ?? '',
        });
        onSuccess?.(res.updatedCount);
      }
      onClose();
    } catch {
      onError?.(assigneeLabels.changeError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{assigneeLabels.changeAction}</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton} disabled={submitting}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>

      {/* 対象表示（#2642）: single=ファイル名 / bulk=○件のOCRデータ
          ファイル名が長い場合は横スクロールを出さず、1行省略表示（…）＋ツールチップで全文をフォローする */}
      {((): ReactElement | null => {
        const isBulk = mode === 'bulk';
        const text = isBulk
          ? assigneeLabels.bulkTargetCountText(targetResultIds.length)
          : (targetLabel ?? '');
        if (!text) return null;
        const label = (
          <Typography
            sx={{
              ...regularMd,
              color: gray80,
              marginBottom: '12px',
              // 長いファイル名でダイアログに横スクロールを出さないため1行省略表示にする
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </Typography>
        );
        // ファイル名（single）は省略され得るのでツールチップで全文を表示。bulk は短い固定文言のため不要。
        return isBulk ? label : <Tooltip title={text}>{label}</Tooltip>;
      })()}

      {/* 常に Select を表示してダイアログ高さを安定化（#2642）
            - ローディング中: disabled + プレースホルダー「読み込み中...」+ 右端にスピナー
            - 候補0件: disabled + プレースホルダー「選択可能なユーザーがいません」
            - 通常: 選択可能 */}
      <Select
        displayEmpty
        sx={styles.select}
        value={loading || options.length === 0 ? '' : selectedUid}
        onChange={(e): void => setSelectedUid(e.target.value)}
        size="small"
        disabled={loading || options.length === 0}
        renderValue={(value: string): ReactElement | undefined => {
          if (loading) {
            return <span style={{ color: gray60 }}>読み込み中...</span>;
          }
          if (options.length === 0) {
            return <span style={{ color: gray60 }}>選択可能なユーザーがいません</span>;
          }
          if (!value) {
            return <span style={{ color: gray60 }}>{assigneeLabels.selectPlaceholder}</span>;
          }
          const selected = options.find(o => o.uid === value);
          if (!selected) {
            return <span style={{ color: gray60 }}>{assigneeLabels.withdrawnLabel}</span>;
          }
          return (
            <Box sx={styles.menuItemBox}>
              <Avatar src={selected.iconUrl || NoAccountIcon} sx={styles.avatar} />
              <span>{selected.name}</span>
            </Box>
          );
        }}
      >
        <MenuItem value="">{assigneeLabels.selectPlaceholder}</MenuItem>
        {options.map(opt => (
          <MenuItem key={opt.uid} value={opt.uid}>
            <Box sx={styles.menuItemBox}>
              <Avatar src={opt.iconUrl || NoAccountIcon} sx={styles.avatar} />
              <span>{opt.name}</span>
            </Box>
          </MenuItem>
        ))}
      </Select>

      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose} disabled={submitting}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button
          sx={styles.confirmButton}
          onClick={(): void => {
            void handleConfirm();
          }}
          disabled={!selectedUid || submitting || loading || options.length === 0}
        >
          <Typography sx={styles.confirmButtonText}>
            {submitting ? assigneeLabels.confirmButtonProcessing : assigneeLabels.confirmButton}
          </Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default AssigneeChangeDialog;
